import { Grid } from '@mui/material';
import { VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { HeaderContent } from '../../../../../components/HeaderContent';
import * as routing from '../../../../../constants/Routing';
import { sortDataByTableColumns, TableColumnsSortConfig } from '../../../../../context/shared/tableColumns';
import { Organization } from '../../../../../domain/backoffice/organization/Organization';
import { useRetrieveAdminUser } from '../../../../../hooks/user/useRetrieveAdminUser';
import { useRetrieveUser } from '../../../../../hooks/user/useRetrieveUser';
import { DataTable } from '../../../../../react-components/data-table/DataTable';
import Text from '../../../../../react-components/Text';
import { OrganizationsTableColumnKeys } from '../../../context/tableColumns/organizations';
import { useSearchOrganizations } from '../../../hooks/organizations/useSearchOrganizations';
import { useSetOrganizationsColumns } from '../../../hooks/organizations/useSetOrganizationsColumns';
import { getOrganizationsColumns } from './BackofficeClientsOrganizationsListColumns';
import { BackofficeClientsOrganizationsListFilters } from './BackofficeClientsOrganizationsListFilters';

export const BackofficeClientsOrganizationsList = () => {
  const navigate = useNavigate();
  const { data: user } = useRetrieveUser();
  const { data: adminUser } = useRetrieveAdminUser();
  const { tableColumns } = useSetOrganizationsColumns();

  const [searchParameters, setSearchParameters] = useState<{ limit: number; offset: number }>({
    offset: 0,
    limit: 20,
  });

  const [sortOrganizationsInfo, setSortOrganizationsInfo] = useState<
    TableColumnsSortConfig<OrganizationsTableColumnKeys>
  >({
    orderBy: `created_at`,
    order: `desc`,
  });

  const {
    data: organizationsPaginated,
    run: searchOrganizations,
    isLoading,
  } = useSearchOrganizations({
    offset: searchParameters.offset,
    limit: searchParameters.limit,
    sortBy: sortOrganizationsInfo.orderBy,
    sortDirection: sortOrganizationsInfo.order,
  });

  useEffect(() => {
    searchOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParameters, sortOrganizationsInfo]);

  const { docs, limit, page, pages, total } = organizationsPaginated || {};

  const goToOrganizationDetails = ({ id }: { id: string }) => {
    navigate(`${routing.BACKOFFICE.CLIENTS.ORGANIZATIONS}/details/${id}`);
  };

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      style={{ backgroundColor: `white`, padding: `16px 22px`, zIndex: 2, height: `100%`, overflowY: `auto` }}
    >
      <Grid container direction="column" style={{ padding: `16px 22px`, height: `100%`, width: `100%` }} wrap="nowrap">
        <Grid
          style={{ paddingTop: `8px`, paddingBottom: `16px` }}
          container
          item
          justifyContent="space-between"
          direction="row"
          wrap="nowrap"
        >
          <HeaderContent
            titleIntlId={`backoffice.clients.organizations_num`}
            titleIntlValues={{ num: !!total && total > 0 ? total : `` }}
            styles={{
              container: {
                backgroundColor: `transparent`,
                width: `unset`,
              },
              title: {
                width: `200px`,
              },
            }}
          />
          <BackofficeClientsOrganizationsListFilters />
        </Grid>

        {isLoading || (total !== undefined && total > 0) ? (
          <Grid item container style={{ overflowX: `auto`, overflowY: `hidden`, height: `100%`, width: `100%` }}>
            {isLoading || (docs ?? []).length > 0 ? (
              <Grid container style={{ width: `100%`, height: `100%`, position: `relative` }}>
                <div style={{ height: `100%`, width: `100%`, overflowX: `auto`, position: `absolute` }}>
                  <DataTable
                    loading={isLoading}
                    showCheckboxes={false}
                    docs={sortDataByTableColumns({
                      data: docs ?? [],
                      tableColumns,
                      sortInfo: sortOrganizationsInfo,
                    }).map((organization: Organization) => ({ doc: organization }))}
                    limit={limit}
                    page={page}
                    pages={pages}
                    total={total ?? 0}
                    noFooter={isLoading}
                    onChangePage={({ offset, limit }) =>
                      setSearchParameters({
                        ...searchParameters,
                        offset,
                        limit,
                      })
                    }
                    hover
                    sortBy={sortOrganizationsInfo.orderBy}
                    sortDirection={sortOrganizationsInfo.order}
                    onColumnSort={(column: any, order: `asc` | `desc`) => {
                      setSortOrganizationsInfo({ order, orderBy: column });
                    }}
                    columns={[
                      ...getOrganizationsColumns({
                        loggedUser: user,
                        adminUser,
                        tableColumns,
                        onRefreshContent: () => searchOrganizations(),
                        goToOrganizationDetails,
                      }),
                    ]}
                  />
                </div>
              </Grid>
            ) : (
              <Grid
                container
                direction="column"
                alignItems="center"
                style={{
                  height: `100%`,
                  padding: `32px`,
                }}
              >
                <Text variant="body-large" intlId="no_results" />
              </Grid>
            )}
          </Grid>
        ) : (
          <Grid
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
            style={{ height: `100%`, width: `100%`, marginTop: `50px` }}
          >
            <VSpacer medium />
            <Text variant="body-medium" intlId="backoffice.clients.no_results_with_this_filters" />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
